import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FeedbackPhase, FeedbackPopover } from 'js/feedback/FeedbackPopover';
import {
  localFeedbackPersistence,
  serverFeedbackPersistence,
} from 'js/feedback/persistence';
import {
  DashboardOverlay,
  DashboardOverlayProvider,
  useOutranked,
  useRegisterOverlay,
} from 'js/overlays/overlayRegistry';
import {
  alertPreviewFeedback,
  analyticsFeedbackData,
  trackAnalyticsFeedback,
  writeAnalyticsFeedback,
} from 'js/analytics_v2/feedbackEvents';

type LegacyAnalyticsFeedbackProps = {
  viewed_user_id: number;
  premium: boolean;
  local_only: boolean;
};

const FEEDBACK_PAGE = /^\/activity\/(overview|readers)\/?$/;
// Onsite pings, DS dialogs and Bootstrap modals render outside the overlay registry.
const PAGE_OVERLAY_SELECTOR =
  '#onsite-ping.visible, dialog[open], .modal.in, .modal.show, [aria-modal="true"]';
const ENGAGEMENT_SELECTOR = '.FilterItem:not(.active):not(.disabled)';
const serverPersistence = serverFeedbackPersistence(
  'AnalyticsDashboardFeedback',
);

const pageOverlayOpen = (): boolean =>
  document.querySelector(PAGE_OVERLAY_SELECTOR) !== null;

const PageOverlays: React.FC = () => {
  const [open, setOpen] = useState(pageOverlayOpen);
  useEffect(() => {
    const observer = new MutationObserver(() => setOpen(pageOverlayOpen()));
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'open', 'aria-modal'],
    });
    setOpen(pageOverlayOpen());
    return () => observer.disconnect();
  }, []);
  useRegisterOverlay(DashboardOverlay.Modal, open);
  return null;
};

const Feedback: React.FC<{
  feedback: LegacyAnalyticsFeedbackProps;
  children: React.ReactNode;
}> = ({ feedback, children }) => {
  const location = useLocation();
  const view = location.pathname.match(FEEDBACK_PAGE)?.[1];
  // Other tabs hide the form instead of unmounting it, keeping its draft and show.
  const [lastView, setLastView] = useState(view);
  useEffect(() => {
    if (view) setLastView(view);
  }, [view]);
  const outranked = useOutranked(DashboardOverlay.Feedback);
  const suppressed = outranked || !view;
  const [userInteracted, setUserInteracted] = useState(false);
  const subject = useMemo(
    () => ({
      experience: 'legacy' as const,
      view: lastView ?? 'overview',
      premium: feedback.premium,
      viewedUserId: feedback.viewed_user_id,
    }),
    [lastView, feedback.premium, feedback.viewed_user_id],
  );
  const localOnly = feedback.local_only;
  const persistence = useMemo(
    () =>
      localOnly
        ? localFeedbackPersistence(
            `analytics:v2-feedback-preview:${feedback.viewed_user_id}`,
          )
        : serverPersistence,
    [localOnly, feedback.viewed_user_id],
  );
  const emit = useCallback(
    (phase: FeedbackPhase, rating: number | null, comment: string) => {
      const data = analyticsFeedbackData(subject, phase, rating, comment);
      if (localOnly) return alertPreviewFeedback(phase, data);
      return writeAnalyticsFeedback(data);
    },
    [subject, localOnly],
  );
  const track = (action: string, extra: Record<string, unknown>): void => {
    if (!localOnly) trackAnalyticsFeedback(subject, action, extra);
  };
  const engage = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (suppressed || !(event.target instanceof Element)) return;
    if (event.target.closest(ENGAGEMENT_SELECTOR)) setUserInteracted(true);
  };

  return (
    <div onClickCapture={engage}>
      {children}
      {lastView && <PageOverlays />}
      {lastView && (
        <FeedbackPopover
          commentId="analytics-feedback-comment"
          emit={emit}
          forceShow={
            new URLSearchParams(location.search).get('analytics_feedback') ===
            'show'
          }
          onDismiss={(hadRating) => track('dismiss', { had_rating: hadRating })}
          onShow={(reason) => track('auto_show', { reason })}
          onSubmit={(rating, hasComment) =>
            track('submit', { rating, has_comment: hasComment })
          }
          persistence={persistence}
          suppressed={suppressed}
          userInteracted={userInteracted}
        />
      )}
    </div>
  );
};

export const LegacyAnalyticsFeedback: React.FC<{
  feedback?: LegacyAnalyticsFeedbackProps | null;
  children: React.ReactNode;
}> = ({ feedback, children }) =>
  feedback ? (
    <DashboardOverlayProvider>
      <Feedback feedback={feedback}>{children}</Feedback>
    </DashboardOverlayProvider>
  ) : (
    <>{children}</>
  );
